/**
 * Mission Service
 * Handles mission data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockMissions } from '../mock/mockMission';
import { generateMockMissionDetail } from '../mock/mockMissionDetail';
import { delay } from '../mock/mockData';
import { parseDate, formatDateTime } from '../../utils/dateUtils';
import { getCallSignDisplayName } from '../../utils/generalUtils';
import type { Mission, MissionDetail, MissionFilter, PaginatedMissions, PaginatedMissionMessages } from '../../types/mission';
import { MissionStatus, RefundDecision } from '../../types/mission';

class MissionService {
  private static getFullName(firstName?: string, lastName?: string): string | undefined {
    const fullName = `${firstName || ''} ${lastName || ''}`.trim();
    return fullName || undefined;
  }

  private static replaceMissionParticipantNames(
    text: string | undefined,
    replacements: Array<{ source?: string; target?: string }>
  ): string | undefined {
    if (!text) {
      return text;
    }

    return replacements.reduce((result, replacement) => {
      if (!replacement.source || !replacement.target) {
        return result;
      }

      return result.split(replacement.source).join(replacement.target);
    }, text);
  }

  private static sanitizeMissionDetail(mission: MissionDetail): MissionDetail {
    const assetDisplayName = getCallSignDisplayName(mission.assetCallSign, 'Asset');
    const ghostDisplayName =
      mission.ghostId || mission.ghostCallSign || mission.ghostName
        ? getCallSignDisplayName(mission.ghostCallSign, 'Ghost')
        : undefined;
    const candidateGhostDisplayName =
      mission.candidateGhostId || mission.candidateGhostCallSign || mission.candidateGhostName
        ? getCallSignDisplayName(mission.candidateGhostCallSign, 'Candidate Ghost')
        : undefined;

    const replaceNames = (text?: string) =>
      MissionService.replaceMissionParticipantNames(text, [
        {
          source: MissionService.getFullName(mission.assetFirstName, mission.assetLastName),
          target: assetDisplayName,
        },
        {
          source: MissionService.getFullName(mission.ghostFirstName, mission.ghostLastName),
          target: ghostDisplayName,
        },
        {
          source: MissionService.getFullName(
            mission.candidateGhostFirstName,
            mission.candidateGhostLastName
          ),
          target: candidateGhostDisplayName,
        },
        { source: mission.assetName, target: assetDisplayName },
        { source: mission.ghostName, target: ghostDisplayName },
        { source: mission.candidateGhostName, target: candidateGhostDisplayName },
      ]);

    return {
      ...mission,
      assetName: assetDisplayName,
      ghostName: ghostDisplayName,
      candidateGhostName: candidateGhostDisplayName,
      assignee: mission.assignee
        ? {
            ...mission.assignee,
            name: ghostDisplayName || getCallSignDisplayName(mission.ghostCallSign, 'Ghost'),
          }
        : mission.assignee,
      evidence: mission.evidence?.map((item) => ({
        ...item,
        uploadedBy: item.uploadedBy
          ? ghostDisplayName || replaceNames(item.uploadedBy)
          : item.uploadedBy,
      })),
      attachments: mission.attachments?.map((item) => ({
        ...item,
        uploadedBy: item.uploadedBy ? assetDisplayName : item.uploadedBy,
      })),
      report: mission.report
        ? {
            ...mission.report,
            createdBy: mission.report.createdBy
              ? ghostDisplayName || assetDisplayName
              : mission.report.createdBy,
          }
        : mission.report,
      statusHistory: mission.statusHistory?.map((item) => ({
        ...item,
        description: replaceNames(item.description),
        userName: item.userName ? replaceNames(item.userName) : item.userName,
      })),
      activities: mission.activities?.map((item) => ({
        ...item,
        description: replaceNames(item.description) || item.description,
        userName: item.userName ? replaceNames(item.userName) : item.userName,
      })),
    };
  }

  /**
   * Convert API mission format to internal Mission format
   */
  private static convertMissionFromApi(apiMission: any): Mission {
    const ghostTier =
      apiMission.ghostTier ??
      apiMission.tier ??
      apiMission.requiredGhostTier ??
      apiMission.ghostTierDisplay;

    return {
      id: apiMission.id,
      code: apiMission.code,
      title: apiMission.title,
      missionType: apiMission.missionType,
      category: apiMission.category,
      ghostTier,
      status: apiMission.status,
      startDate: apiMission.timeframe?.startDate ?? apiMission.startDate,
      endDate: apiMission.timeframe?.endDate ?? apiMission.endDate,
      createdAt: apiMission.createdAt,
      updatedAt: apiMission.updatedAt,
      assetId: apiMission.assetId,
      assetFirstName: apiMission.assetFirstName,
      assetLastName: apiMission.assetLastName,
      assetCallSign: apiMission.assetCallSign,
      assetEmail: apiMission.assetEmail,
      ghostId: apiMission.ghostId,
      ghostFirstName: apiMission.ghostFirstName,
      ghostLastName: apiMission.ghostLastName,
      ghostCallSign: apiMission.ghostCallSign,
      ghostEmail: apiMission.ghostEmail,
      candidateGhostId: apiMission.candidateGhostId,
      candidateGhostFirstName: apiMission.candidateGhostFirstName,
      candidateGhostLastName: apiMission.candidateGhostLastName,
      candidateGhostCallSign: apiMission.candidateGhostCallSign,
      candidateGhostEmail: apiMission.candidateGhostEmail,
    };
  }

  /**
   * Convert API mission detail format to internal MissionDetail format
   */
  private static convertMissionDetailFromApi(apiMission: any): MissionDetail {
    const baseMission = MissionService.convertMissionFromApi(apiMission);

    return MissionService.sanitizeMissionDetail({
      ...baseMission,

      // Basic Info
      title: apiMission.title,
      description: apiMission.description,
      createdText: formatDateTime(apiMission.createdAt),
      lastUpdatedText: formatDateTime(apiMission.updatedAt),

      // Location & Timeframe
      location: apiMission.location ? {
        address: apiMission.location.address,
        city: apiMission.location.city,
        state: apiMission.location.state,
        coordinates: apiMission.location.latitude && apiMission.location.longitude ? {
          latitude: apiMission.location.latitude,
          longitude: apiMission.location.longitude,
        } : undefined,
      } : undefined,

      timeframe: apiMission.timeframe ? {
        startDate: formatDateTime(apiMission.timeframe.startDate),
        endDate: formatDateTime(apiMission.timeframe.endDate),
      } : undefined,

      // User Info
      assetId: apiMission.assetId,
      assetName: getCallSignDisplayName(apiMission.assetCallSign, 'Asset'),
      ghostId: apiMission.ghostId,
      ghostName: apiMission.ghostId
        ? getCallSignDisplayName(apiMission.ghostCallSign, 'Ghost')
        : undefined,
      candidateGhostName: apiMission.candidateGhostId
        ? getCallSignDisplayName(apiMission.candidateGhostCallSign, 'Candidate Ghost')
        : undefined,

      // Category & Questions
      categoryName: apiMission.category,
      subcategoryName: apiMission.subcategory,
      categoryAnswers: Array.isArray(apiMission.categoryAnswers)
        ? apiMission.categoryAnswers.reduce((answers: Record<string, any>, item: any) => {
          if (item.question && item.answer !== undefined) {
            answers[item.question] = item.answer;
          }
          return answers;
        }, {})
        : {},

      // Evidence & Attachments
      evidence: apiMission.evidences?.map((evidence: any) => {
        const capturedAt = evidence.attachments?.[0]?.metadata?.capturedAt;

        return {
          id: evidence.id,
          type: evidence.attachments?.[0]?.metadata?.contentType?.startsWith('video/') ? 'video' : 'photo',
          url: evidence.attachments?.[0]?.url || '',
          fileName: evidence.attachments?.[0]?.name,
          uploadedAt: capturedAt ? parseDate(capturedAt) : new Date(0),
          uploadedAtText: capturedAt ? formatDateTime(capturedAt) : '',
        };
      }),

      attachments: apiMission.attachments?.map((attachment: any) => {
        const fileName = attachment.name;
        const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';

        // Derive mimeType from file extension
        let mimeType = 'application/octet-stream'; // default
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension)) {
          mimeType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`;
        } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(fileExtension)) {
          mimeType = `video/${fileExtension}`;
        } else if (fileExtension === 'pdf') {
          mimeType = 'application/pdf';
        } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(fileExtension)) {
          mimeType = 'application/zip';
        }

        return {
          id: fileName,
          fileName,
          fileSize: 0,
          mimeType,
          url: attachment.url,
          uploadedAt: attachment.uploadedAt ? parseDate(attachment.uploadedAt) : new Date(0),
          uploadedAtText: attachment.uploadedAt ? formatDateTime(attachment.uploadedAt) : '',
        };
      }),

      // Pricing
      totalPrice: apiMission.totalPrice,
      assetBookingFee: apiMission.assetBookingFee,
      platformFee: apiMission.platformFee,
      ghostEarnings: apiMission.ghostEarnings,
      estimatedCompletionHours: apiMission.hours,

      // Debrief
      reportWeThink: apiMission.debrief?.weThink,
      reportConfidence: apiMission.debrief?.confidence,
      reportHereWhy: apiMission.debrief?.heresWhy,

      // Admin Review
      reviewDecision: apiMission.reviewDecision,
      reviewReason: apiMission.reviewReason,
      cancellationReason: apiMission.cancellationReason,
      canApproveReview: apiMission.canApproveReview ?? false,
      canRejectReview: apiMission.canRejectReview ?? false,
      latestPaymentStatus: apiMission.latestPaymentStatus,
      cancellationReason: apiMission.cancellationReason ?? apiMission.CancellationReason,
      cancelledAtUtc: apiMission.cancelledAtUtc ?? apiMission.CancelledAtUtc,
      cancelledAtText: (apiMission.cancelledAtUtc ?? apiMission.CancelledAtUtc)
        ? formatDateTime(apiMission.cancelledAtUtc ?? apiMission.CancelledAtUtc)
        : undefined,
      cancelledBy: apiMission.cancelledBy ?? apiMission.CancelledBy,
      refundDecision: apiMission.refundDecision ?? apiMission.RefundDecision,
      refundAmount: apiMission.refundAmount ?? apiMission.RefundAmount,

      // Status History / Timeline
      statusHistory: apiMission.timeline?.map((item: any) => ({
        status: item.title || item.status || '',
        timestamp: item.createdAt ? parseDate(item.createdAt) : new Date(),
        timestampText: item.createdAt ? formatDateTime(item.createdAt) : '',
        description: item.description,
        userId: item.userId,
        userName: item.actorName,
      })),
    });
  }

  /**
   * Get paginated missions with optional filters
   */
  static async getMissions(
    page: number = 1,
    pageSize: number = 5,
    filter?: MissionFilter
  ): Promise<PaginatedMissions> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredMissions = [...mockMissions];

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        if (Array.isArray(filter.status)) {
          // Multiple statuses selected
          filteredMissions = filteredMissions.filter(m => filter.status!.includes(m.status));
        } else {
          // Single status
          filteredMissions = filteredMissions.filter(m => m.status === filter.status);
        }
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredMissions = filteredMissions.filter(m =>
          m.code.toLowerCase().includes(searchLower) ||
          m.title.toLowerCase().includes(searchLower) ||
          m.assetId.toLowerCase().includes(searchLower)
        );
      }

      // Apply User ID filter
      if (filter?.userId) {
        filteredMissions = filteredMissions.filter(m => m.assetId === filter.userId || m.ghostId === filter.userId);
      }

      // Apply date range filter
      if (filter?.dateRange) {
        filteredMissions = filteredMissions.filter(m => {
          const createdAt = new Date(m.createdAt);
          const afterStart = !filter.dateRange?.start || createdAt >= filter.dateRange.start;
          const beforeEnd = !filter.dateRange?.end || createdAt <= filter.dateRange.end;
          return afterStart && beforeEnd;
        });
      }

      const total = filteredMissions.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredMissions.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      const params: Record<string, string | string[]> = {
        Page: page.toString(),
        PageSize: pageSize.toString(),
      };

      // Add optional filters
      if (filter?.status && filter.status !== 'all') {
        params.Status = filter.status;
      }
      if (filter?.search) {
        params.Search = filter.search;
      }
      if (filter?.userId) {
        params.UserId = filter.userId;
      }

      if (filter?.dateRange) {
        if (filter.dateRange.start) {
          params.StartDate = filter.dateRange.start.toISOString();
        }
        if (filter.dateRange.end) {
          params.EndDate = filter.dateRange.end.toISOString();
        }
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: {
          total: number;
          items: Mission[];
        };
        message?: string;
      }>('/admin/missions', { params });

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch missions');
      }

      // Convert API response to expected format
      return {
        data: response.data.items.map(MissionService.convertMissionFromApi),
        total: response.data.total,
        page,
        pageSize,
        totalPages: Math.ceil(response.data.total / pageSize),
      };
    }
  }

  /**
   * Get mission by ID
   */
  static async getMissionById(id: string): Promise<Mission | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return mockMissions.find(m => m.id === id || m.code === id) || null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: any;
        message?: string;
      }>(`/admin/missions/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch mission');
      }

      return response.data ? MissionService.convertMissionDetailFromApi(response.data) : null;
    }
  }

  /**
   * Get mission detail by ID
   */
  static async getMissionDetail(id: string): Promise<MissionDetail | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const detail = generateMockMissionDetail(id);
      if (detail) {
        return MissionService.sanitizeMissionDetail(detail);
      }
      // Fallback: try to find basic mission and convert to detail
      const mission = mockMissions.find(m => m.id === id || m.code === id);
      if (mission) {
        return MissionService.sanitizeMissionDetail({
          ...mission,
          title: mission.title,
          description: `Mission ${mission.code} details`,
        } as MissionDetail);
      }
      return null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: any;
        message?: string;
      }>(`/admin/missions/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch mission detail');
      }

      return response.data ? MissionService.convertMissionDetailFromApi(response.data) : null;
    }
  }

  /**
   * Cancel a mission
   */
  static async cancelMission(
    id: string,
    options?: {
      reason?: string;
      decision?: RefundDecision;
      refundAmount?: number;
    }
  ): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const mission = mockMissions.find(m => m.id === id || m.code === id);
      if (!mission) {
        throw new Error('Mission not found');
      }

      // Check if mission is already completed - cannot cancel completed missions
      if (mission.status === MissionStatus.COMPLETED) {
        throw new Error('Cannot cancel a mission that is already completed');
      }

      // Check if mission is under review - cannot cancel missions under review
      if (mission.status === MissionStatus.UNDER_REVIEW) {
        throw new Error('Cannot cancel a mission that is under review');
      }

      // Check if mission is already in a final state
      if (mission.status === MissionStatus.CANCELLED ||
        mission.status === MissionStatus.EXPIRED ||
        mission.status === MissionStatus.CLOSED) {
        throw new Error('Mission is already in a final state');
      }

      // Note: In real implementation, the status would be set to a cancelled status
      // For now, we'll mark it as cancelled (though this status doesn't exist in the new enum)
      // The backend should handle this properly
      mission.updatedAt = new Date().toISOString();
      // Store cancellation reason if provided
      if (options?.reason) {
        console.log('Cancellation reason:', options.reason);
      }
      return true;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/missions/${id}/cancel`, {
        reason: options?.reason ?? '',
        decision: options?.decision ?? RefundDecision.None,
        refundAmount: options?.refundAmount ?? 0,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to cancel mission');
      }

      return true;
    }
  }


  /**
   * Approve mission admin review
   */
  static async approveMissionReview(id: string): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const mission = mockMissions.find(m => m.id === id || m.code === id);
      if (!mission) {
        throw new Error('Mission not found');
      }

      mission.status = MissionStatus.SUBMITTED;
      mission.updatedAt = new Date().toISOString();
      return true;
    }

    const response = await apiService.post<{
      isSuccess: boolean;
      message?: string;
    }>(`/admin/missions/${id}/approve-review`);

    if (!response.isSuccess) {
      throw new Error(response.message || 'Failed to approve mission review');
    }

    return true;
  }

  /**
   * Reject mission admin review
   */
  static async rejectMissionReview(id: string, reason: string): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const mission = mockMissions.find(m => m.id === id || m.code === id);
      if (!mission) {
        throw new Error('Mission not found');
      }

      mission.status = MissionStatus.CLOSED;
      mission.updatedAt = new Date().toISOString();
      return true;
    }

    const response = await apiService.post<{
      isSuccess: boolean;
      message?: string;
    }>(`/admin/missions/${id}/reject-review`, {
      reason,
    });

    if (!response.isSuccess) {
      throw new Error(response.message || 'Failed to reject mission review');
    }

    return true;
  }

  /**
   * Get mission messages
   */
  public static async getMissionMessages(id: string, params?: { missionId?: string, page?: number, pageSize?: number }): Promise<PaginatedMissionMessages> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // Return mock messages
      return {
        total: 2,
        items: [
          {
            id: 'm1',
            conversationId: 'c1',
            senderUserId: 'u1',
            senderRole: 'Asset',
            messageType: 'Text',
            content: 'Hello, what is the status?',
            createdAt: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: 'm2',
            conversationId: 'c1',
            senderUserId: 'u2',
            senderRole: 'Ghost',
            messageType: 'Text',
            content: 'I am on it.',
            createdAt: new Date(Date.now() - 1800000).toISOString(),
          }
        ]
      };
    }

    const response = await apiService.get<any>(`/admin/missions/${id}/messages`, { params });
    if (response.isSuccess) {
      return response.data;
    }
    throw new Error(response.message || 'Failed to fetch mission messages');
  }


  /**
   * Get mission count by status
   */
  static async getMissionCounts(): Promise<Record<MissionStatus | 'all', number>> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY / 2);

      const counts: Record<MissionStatus | 'all', number> = {
        all: mockMissions.length,
        [MissionStatus.PENDING]: 0,
        [MissionStatus.PENDING_ADMIN_REVIEW]: 0,
        [MissionStatus.SUBMITTED]: 0,
        [MissionStatus.GHOST_ASSIGNED]: 0,
        [MissionStatus.MISSION_IN_PROGRESS]: 0,
        [MissionStatus.COMPLETED]: 0,
        [MissionStatus.UNDER_REVIEW]: 0,
        [MissionStatus.CLOSED]: 0,
        [MissionStatus.CANCELLED]: 0,
        [MissionStatus.EXPIRED]: 0,
      };

      mockMissions.forEach(m => {
        counts[m.status]++;
      });

      return counts;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: Record<MissionStatus | 'all', number>;
        message?: string;
      }>('/missions/counts');

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch mission counts');
      }

      return response.data;
    }
  }
}

export { MissionService };
